import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@volca/ui/lib/toast";
import { useApi } from "@/core/api";
import { payPeriodsKey } from "@/payroll/pay-periods/query";
import type {
  NoteAction,
  PayrollPeriodNote,
  PayrollPeriodNotes,
} from "./types";
import { enrichPeriodNotes } from "./utils";

const notesKey = (businessId: string | null) =>
  ["pay-period-notes", businessId] as const;

type ScopedNoteAction = NoteAction & {
  businessId: string | null;
  periodStart: string;
};

const SUCCESS_MESSAGES = {
  address: "Note addressed",
  forward: "Note sent to the next open period",
  reopen: "Note reopened",
} satisfies Record<Exclude<NoteAction["action"], "create">, string>;

export const usePayPeriodNotes = (
  businessId: string | null,
  periodStart: string | null,
  { enabled = true, poll = false }: { enabled?: boolean; poll?: boolean } = {},
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...notesKey(businessId), periodStart],
    queryFn: ({ signal }) =>
      api
        .get<PayrollPeriodNotes>(
          `/businesses/${businessId}/pay_periods/${periodStart}/notes`,
          { signal },
        )
        .then((response) => response.data),
    select: enrichPeriodNotes,
    enabled: enabled && !!businessId && !!periodStart,
    refetchInterval: poll ? 15_000 : false,
    refetchOnWindowFocus: true,
  });
};

export const usePayPeriodNoteAction = (
  businessId: string | null,
  periodStart: string,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({
      businessId,
      periodStart,
      ...input
    }: ScopedNoteAction): Promise<PayrollPeriodNote> => {
      const path = `/businesses/${businessId}/pay_periods/${periodStart}/notes`;
      const response =
        input.action === "create"
          ? await api.post<PayrollPeriodNote>(path, {
              body: input.body,
              grid_text: input.grid_text,
            })
          : await api.patch<PayrollPeriodNote>(
              `${path}/${input.id}/${input.action}`,
            );
      return response.data;
    },
    onSuccess: (_note, input) => {
      if (input.action !== "create") {
        toast.success(SUCCESS_MESSAGES[input.action]);
      }
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: notesKey(input.businessId) }),
        queryClient.invalidateQueries({
          queryKey: payPeriodsKey(input.businessId),
        }),
      ]);
    },
    onError: (_error, input) => {
      void queryClient.invalidateQueries({
        queryKey: notesKey(input.businessId),
      });
    },
  });
  // A queued request must keep its original target if the page changes.
  return {
    ...mutation,
    mutate: (
      input: NoteAction,
      options?: Parameters<typeof mutation.mutate>[1],
    ) => mutation.mutate({ ...input, businessId, periodStart }, options),
    mutateAsync: (
      input: NoteAction,
      options?: Parameters<typeof mutation.mutateAsync>[1],
    ) => mutation.mutateAsync({ ...input, businessId, periodStart }, options),
  };
};
